export default function CookiePolicyPage() {
  return (
    <div className="bg-white dark:bg-slate-900">
      {/* Hero */}
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-5xl">
            Cookie Policy
          </h1>
          <p className="mt-6 text-lg text-slate-600 dark:text-slate-300">
            Effective Date: 16 May 2026
          </p>
        </div>
      </div>

      {/* Intro */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl space-y-4">
            <p className="text-slate-600 dark:text-slate-300">
              This Cookie Policy explains how CyberActions Limited
              (&ldquo;CyberActions&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;)
              uses cookies and similar technologies when you visit our website and platform.
            </p>
            <p className="text-slate-600 dark:text-slate-300">
              When you first visit our website, you will be presented with a cookie preference
              banner. By selecting your preferences, you consent to our use of non-essential
              cookies in accordance with this policy. You can change or adjust your consent
              preferences at any time by revisiting our cookie settings panel.
            </p>
          </div>
        </div>
      </div>

      {/* 1. What Are Cookies? */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              1. What Are Cookies?
            </h2>
            <div className="mt-6 space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                Cookies are small text files placed on your device when you visit a website. They
                help websites function properly, improve user experience, enhance security, and
                provide analytical information to the site owners.
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                Cookies may be temporary (&ldquo;session cookies,&rdquo; which expire when you close
                your browser) or &ldquo;persistent cookies,&rdquo; which remain stored on your
                device for a defined period or until manually deleted.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 2. How We Use Cookies */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              2. How We Use Cookies
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              CyberActions Limited uses cookies for the following purposes:
            </p>

            {/* Essential */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Essential Cookies
              </h3>
              <p className="mt-2 text-slate-600 dark:text-slate-300">
                These cookies are mandatory for the basic operation, performance, and security of
                our website and automated platform services.
              </p>
              <p className="mt-4 text-slate-600 dark:text-slate-300">Examples include:</p>
              <ul className="mt-2 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>User authentication and secure login maintenance</li>
                <li>Multi-tenant session state management</li>
                <li>Real-time security tracking and fraud prevention</li>
                <li>System load balancing and interface functionality</li>
              </ul>
              <p className="mt-4 text-slate-600 dark:text-slate-300">
                Without these strictly necessary cookies, core aspects of our platform cannot
                function correctly.
              </p>
            </div>

            {/* Analytics */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Analytics Cookies
              </h3>
              <p className="mt-2 text-slate-600 dark:text-slate-300">
                We utilize aggregated analytics tools to understand how prospective partners and
                clients interact with our public infrastructure.
              </p>
              <p className="mt-4 text-slate-600 dark:text-slate-300">This tracking allows us to:</p>
              <ul className="mt-2 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>Benchmark and optimize website performance</li>
                <li>Identify platform usage trends and popular feature modules</li>
                <li>Enhance user experience across multiple devices</li>
                <li>Monitor general application stability and reliability</li>
              </ul>
              <p className="mt-4 text-slate-600 dark:text-slate-300">
                All captured analytical indicators are completely anonymized and aggregated,
                meaning they do not directly identify individual users.
              </p>
            </div>

            {/* Functional */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Functional Cookies
              </h3>
              <p className="mt-2 text-slate-600 dark:text-slate-300">
                These functional markers allow our system to remember your custom preferences and
                configured configurations, including:
              </p>
              <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>Interface language preferences</li>
                <li>Saved session states</li>
                <li>Custom dashboard or user interface layout configurations</li>
              </ul>
            </div>

            {/* Third-Party */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Third-Party Cookies
              </h3>
              <p className="mt-2 text-slate-600 dark:text-slate-300">
                Certain enterprise tools and integrated APIs embedded into our cloud application
                framework may deploy independent cookies onto your local machine. These include
                integrations with:
              </p>
              <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>Microsoft Graph API and associated directory infrastructure</li>
                <li>Google Workspace Admin API and authentication layers</li>
                <li>Third-party secure identity providers (OAuth workflows)</li>
                <li>Encrypted content delivery platforms</li>
              </ul>
              <p className="mt-4 text-slate-600 dark:text-slate-300">
                These third-party networks maintain entirely separate, autonomous privacy and
                cookie policies.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Managing Cookies */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              3. Managing Cookies
            </h2>
            <div className="mt-6 space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                You maintain absolute structural control over cookie access permissions. You can
                adjust, block, or delete cookies via our on-site preference banner or directly
                inside your active web browser settings.
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                Most modern desktop and mobile browsers enable you to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>View all currently stored cookie files</li>
                <li>Selectively purge individual third-party tracking scripts</li>
                <li>Enforce strict blocking rules against third-party cookies</li>
                <li>Completely disable all local cookie storage options</li>
              </ul>
              <p className="text-slate-600 dark:text-slate-300">
                Please note that adjusting or entirely blocking core tracking permissions can
                instantly disrupt the secure functionality, login persistence, and overall
                usability of the CyberActions platform.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Data Protection */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              4. Data Protection
            </h2>
            <div className="mt-6 space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                Any operational or security data systematically processed through our cookie
                framework is handled strictly in line with our comprehensive Privacy Policy and
                applicable UK data privacy guidelines, including the UK GDPR and the Data
                Protection Act 2018.
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                CyberActions Limited implements rigid technical and organizational safeguard
                controls to ensure all platform activity and tracking telemetry remain strictly
                insulated.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Changes to This Policy */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              5. Changes to This Policy
            </h2>
            <div className="mt-6 space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                We may periodically revise this Cookie Policy to match evolving technical updates,
                infrastructure changes, or legal updates under UK compliance rules.
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                The newest operational version will always stay prominently pinned to this URL
                layout alongside its updated effective date timestamp.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 6. Contact Us */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">6. Contact Us</h2>
            <div className="mt-6 space-y-2">
              <p className="text-slate-600 dark:text-slate-300">
                For any technical data protection inquiries or to update your cookie consent
                framework, contact our security registry:
              </p>
              <p className="mt-4 text-slate-600 dark:text-slate-300">
                <span className="font-semibold text-slate-900 dark:text-white">
                  CyberActions Limited
                </span>
                <br />
                6th Floor, 66 Paul Street
                <br />
                London, EC2A 4NE
                <br />
                United Kingdom
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                Email:{' '}
                <a
                  href="mailto:contact@cyberactions.co.uk"
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  contact@cyberactions.co.uk
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
